import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
// ─── Types ─────────────────────────────────────────────────────────────────────

export interface FollowerItem {
  id: string;
  type: 'user' | 'contact';
  name: string;
  email?: string;
  picture?: string;
}

export interface FollowerSelectorProps {
  value: FollowerItem[];
  onChange: (followers: FollowerItem[]) => void;
  disabled?: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const USER_CHIP_BG = '#ede9fe';
const USER_CHIP_TEXT = '#8b5cf6';
const CONTACT_CHIP_BG = '#dbeafe';
const CONTACT_CHIP_TEXT = '#3b82f6';

// ─── Search Hook ───────────────────────────────────────────────────────────────

interface UserDropdownItem {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface ContactDropdownItem {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

function useFollowerSearch(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const usersQuery = useQuery({
    queryKey: ['follower-search-users', debouncedQuery],
    queryFn: async () => {
      const response = await api.get('users/dropdown', { params: { status: 'active' } });
      const users = response.data.data as Array<{ id: string; firstName: string; lastName?: string; email: string; avatar?: string }>;
      const searchLower = debouncedQuery.toLowerCase();
      return users
        .map((u) => ({
          id: u.id,
          name: `${u.firstName}${u.lastName ? ` ${u.lastName}` : ''}`.trim(),
          email: u.email,
          avatar: u.avatar,
        }))
        .filter(
          (u) =>
            u.name.toLowerCase().includes(searchLower) ||
            u.email?.toLowerCase().includes(searchLower)
        );
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const contactsQuery = useQuery({
    queryKey: ['follower-search-contacts', debouncedQuery],
    queryFn: async () => {
      const response = await api.get('contacts/dropdown');
      const contacts = response.data.data as ContactDropdownItem[];
      const searchLower = debouncedQuery.toLowerCase();
      return contacts.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchLower) ||
          c.email?.toLowerCase().includes(searchLower) ||
          c.phone?.includes(debouncedQuery)
      );
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    users: (usersQuery.data ?? []) as UserDropdownItem[],
    contacts: (contactsQuery.data ?? []) as ContactDropdownItem[],
    isLoading: usersQuery.isLoading || contactsQuery.isLoading,
    isReady: debouncedQuery.length >= 2,
  };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const FollowerSelector: React.FC<FollowerSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showSearch, setShowSearch] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { users, contacts, isLoading, isReady } = useFollowerSearch(searchQuery);

  // Filter out already-selected followers
  const selectedIds = new Set(value.map((f) => `${f.type}:${f.id}`));
  const filteredUsers = users.filter((u) => !selectedIds.has(`user:${u.id}`));
  const filteredContacts = contacts.filter((c) => !selectedIds.has(`contact:${c.id}`));
  const totalResults = filteredUsers.length + filteredContacts.length;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setShowSearch(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build flat list of results for keyboard navigation
  const flatResults: Array<{ item: UserDropdownItem | ContactDropdownItem; type: 'user' | 'contact' }> = [
    ...filteredUsers.map((u) => ({ item: u, type: 'user' as const })),
    ...filteredContacts.map((c) => ({ item: c, type: 'contact' as const })),
  ];

  const handleSelect = useCallback(
    (item: UserDropdownItem | ContactDropdownItem, type: 'user' | 'contact') => {
      const follower: FollowerItem = {
        id: item.id,
        type,
        name: item.name,
        email: item.email,
        picture: type === 'user' ? (item as UserDropdownItem).avatar : undefined,
      };
      onChange([...value, follower]);
      setSearchQuery('');
      setHighlightedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (id: string, type: 'user' | 'contact') => {
      onChange(value.filter((f) => !(f.id === id && f.type === type)));
    },
    [value, onChange]
  );

  const handleAddClick = () => {
    setShowSearch(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || flatResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && flatResults[highlightedIndex]) {
        const { item, type } = flatResults[highlightedIndex];
        handleSelect(item, type);
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setShowSearch(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  const showDropdown = isDropdownOpen && isReady;

  // Track the boundary index between users and contacts in flatResults
  const userCount = filteredUsers.length;

  return (
    <div ref={containerRef} className="w-full">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: USER_CHIP_TEXT }}
          />
          <span className="text-xs" style={{ color: '#64748b' }}>
            Usuários internos
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: CONTACT_CHIP_TEXT }}
          />
          <span className="text-xs" style={{ color: '#64748b' }}>
            Contatos externos
          </span>
        </div>
      </div>

      {/* Chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((follower) => {
            const isUser = follower.type === 'user';
            const chipBg = isUser ? USER_CHIP_BG : CONTACT_CHIP_BG;
            const chipText = isUser ? USER_CHIP_TEXT : CONTACT_CHIP_TEXT;

            return (
              <span
                key={`${follower.type}:${follower.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: chipBg, color: chipText }}
              >
                {follower.picture ? (
                  <img
                    src={follower.picture}
                    alt={follower.name}
                    className="w-4 h-4 rounded-full object-cover"
                  />
                ) : (
                  <Icon name="user" className="w-3 h-3" />
                )}
                <span className="truncate max-w-[120px]">{follower.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(follower.id, follower.type)}
                    className="ml-0.5 rounded-full p-0.5 transition-colors"
                    style={{ color: chipText }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isUser
                        ? 'rgba(139, 92, 246, 0.15)'
                        : 'rgba(59, 130, 246, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Search Input / Add Button */}
      {showSearch ? (
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsDropdownOpen(true);
              setHighlightedIndex(-1);
            }}
            onFocus={() => {
              if (searchQuery.length >= 2) setIsDropdownOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Buscar usuário ou contato..."
            icon={'search'}
            iconPosition="left"
            disabled={disabled}
          />
          {isLoading && searchQuery.length >= 2 && (
            <Icon name="loader" className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin"
              style={{ color: '#8b5cf6' }} />
          )}

          {/* Dropdown */}
          {showDropdown && (
            <div
              className="absolute left-0 right-0 z-50 mt-1 rounded-lg overflow-hidden"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow:
                  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                maxHeight: '280px',
                overflowY: 'auto',
              }}
            >
              {/* Loading */}
              {isLoading && (
                <div className="flex items-center gap-2 px-3 py-3">
                  <Icon name="loader" className="w-4 h-4 animate-spin" style={{ color: '#8b5cf6' }} />
                  <span className="text-sm" style={{ color: '#64748b' }}>
                    Buscando...
                  </span>
                </div>
              )}

              {/* Users group */}
              {!isLoading && filteredUsers.length > 0 && (
                <>
                  <div
                    className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: '#8b5cf6', backgroundColor: '#faf5ff' }}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon name="users" className="w-3 h-3" />
                      Usuários
                    </div>
                  </div>
                  {filteredUsers.map((user, index) => (
                    <button
                      key={`user:${user.id}`}
                      type="button"
                      className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors"
                      style={{
                        backgroundColor:
                          highlightedIndex === index ? '#f5f3ff' : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f3ff';
                        setHighlightedIndex(index);
                      }}
                      onMouseLeave={(e) => {
                        if (highlightedIndex !== index) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      onClick={() => handleSelect(user, 'user')}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                        style={{ backgroundColor: USER_CHIP_BG }}
                      >
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="w-7 h-7 rounded-full object-cover"
                          />
                        ) : (
                          <Icon name="user" className="w-3.5 h-3.5" style={{ color: USER_CHIP_TEXT }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-medium truncate"
                          style={{ color: '#1e293b' }}
                        >
                          {user.name}
                        </p>
                        {user.email && (
                          <p className="text-xs truncate" style={{ color: '#64748b' }}>
                            {user.email}
                          </p>
                        )}
                      </div>
                      <span
                        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: USER_CHIP_TEXT }}
                      />
                    </button>
                  ))}
                </>
              )}

              {/* Contacts group */}
              {!isLoading && filteredContacts.length > 0 && (
                <>
                  <div
                    className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider"
                    style={{
                      color: CONTACT_CHIP_TEXT,
                      backgroundColor: '#eff6ff',
                      borderTop:
                        filteredUsers.length > 0 ? '1px solid #e5e7eb' : undefined,
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon name="user" className="w-3 h-3" />
                      Contatos
                    </div>
                  </div>
                  {filteredContacts.map((contact, cIndex) => {
                    const flatIndex = userCount + cIndex;
                    return (
                      <button
                        key={`contact:${contact.id}`}
                        type="button"
                        className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors"
                        style={{
                          backgroundColor:
                            highlightedIndex === flatIndex ? '#eff6ff' : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#eff6ff';
                          setHighlightedIndex(flatIndex);
                        }}
                        onMouseLeave={(e) => {
                          if (highlightedIndex !== flatIndex) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={() => handleSelect(contact, 'contact')}
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: CONTACT_CHIP_BG }}
                        >
                          <Icon name="user" className="w-3.5 h-3.5"
                            style={{ color: CONTACT_CHIP_TEXT }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium truncate"
                            style={{ color: '#1e293b' }}
                          >
                            {contact.name}
                          </p>
                          {(contact.email || contact.phone) && (
                            <p className="text-xs truncate" style={{ color: '#64748b' }}>
                              {[contact.email, contact.phone]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: CONTACT_CHIP_TEXT }}
                        />
                      </button>
                    );
                  })}
                </>
              )}

              {/* No results */}
              {!isLoading && totalResults === 0 && isReady && (
                <div className="px-3 py-3 text-center">
                  <p className="text-sm" style={{ color: '#64748b' }}>
                    Nenhum resultado encontrado
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={handleAddClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{ color: '#8b5cf6' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f3ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon name="plus" className="w-4 h-4" />
            Adicionar
          </button>
        )
      )}
    </div>
  );
};

FollowerSelector.displayName = 'FollowerSelector';
